/**
 * Section 1 — Information Collected
 * Exact legal text from the approved Privacy Policy prototype.
 */

import { BulletPoint } from '../BulletPoint';

export const InformationCollected = () => (
  <>
    <h2 className="font-recoleta font-black">1. Information Collected</h2>
    <div className="font-termina text-xl max-md:text-2xl pt-16 pb-8">
      <p className="w-full font-termina">
        bQuest collects information on our Users (also referred to as "you"
        herein) in various ways, such as by voluntary submissions, participation
        in our Services, activities as a Provider, Client, or Member,
        application to become a Provider, signup information as a Member, from
        third parties and Providers with your consent, and through cookie and
        other tracking technology. bQuest collects the following personal
        information:
      </p>
      <br />
      <br />

      <BulletPoint>
        Personal Information
        <br />
        "Personal information" refers to information that tells us about you,
        such as your name, phone number, email, address, application
        information, information about your objectives you provide to us or your
        Provider(s) in the course of using our Services, and any information
        relating to certain support or customer service issues. In many cases,
        we need this information to provide to you many of the services that you
        may have requested. We may also create recommendations for you based on
        your use of the Services; these recommendations are also personal
        information.
      </BulletPoint>
      <br />

      <BulletPoint>
        Provider Application
        <br />
        When you apply to become a Provider on the bQuest Platform, we may ask
        for your credentials and for other information to verify your identity
        and reputation. We use a third party service provider to evaluate your
        application to become a Provider; we do not see the information you
        provide that third party service but we receive a score which helps us
        evaluate your application.
      </BulletPoint>
      <br />

      <BulletPoint>
        Phone Numbers
        <br />
        By providing a cellular telephone number to us, you expressly consent,
        and authorize us or a third party on our behalf, to deliver account
        communications or text messages to the number you provide. You are not
        required to consent to these communications as a condition to purchasing
        any goods or services, but withholding your consent may prevent us from
        providing certain Services to you. This consent covers all future
        autodialed or prerecorded telemarketing calls and text messages from us
        or a third party placing these calls on our behalf. You may revoke
        consent to receive text messages at any time by contacting bQuest,
        including by following the unsubscribe instructions in the text.
      </BulletPoint>
      <br />

      <BulletPoint>
        Submission of User Content
        <br />
        We collect information that you voluntarily provide as part of our
        Services, including messages, comments, and other feedback you submit
        through the Services such as through responses to a survey (each an
        example of "User Content"). bQuest may in our sole discretion share your
        User Content with others, such as commentary on Providers or Services to
        the relevant Provider, as applicable, provided that we will only share
        messages to Providers with that Provider and our back end third party
        service providers (e.g. hosting provider). If you do not want certain
        information shared with others, you should not submit User Content, or
        should not submit User Content that contains information or personal
        information you do not want shared in this manner.
      </BulletPoint>
      <br />

      <BulletPoint>
        Aggregate Information
        <br />
        We may collect general, non-personal, statistical information about the
        use of the Services, such as how many visitors visit a specific page on
        the bQuest Platform, how long they stay on that page, and which
        hyperlinks, if any, they click on. This information represents a generic
        overview of our Users, including their collective browsing habits, and
        allows us and other third parties to modify information, promotions,
        offers and/or discounts on products and services based on User traffic
        and behavior. We collect this information through the use of
        technologies such as cookies and web beacons, which are discussed in
        greater detail below. We collect this information to help enhance the
        Services, provide you with the Services you request, secure and improve
        our Services, and for any other legal purpose.
      </BulletPoint>
      <br />

      <BulletPoint>
        Third Party Information
        <br />
        We may supplement information you provide to us with information from
        other sources, such as third party service providers that we make
        available to you or guide you to. We use this information to help us
        maintain the accuracy of the information we collect, to evaluate your
        application, to enable us to provide and improve our Services, to target
        our communications so that we can inform you of services and offers that
        may be of interest, and for internal business analysis or any other
        business purposes consistent with our mission. For clarity, we will
        never collect or store Protected Health Information ("PHI") as such term
        is defined by the Health Insurance Portability and Accountability Act
        (HIPAA).
        <br />
        <br />
        In general, we collect the minimum amount of personal information
        possible about you from Providers with whom you interact on and off the
        Services. We may collect more personal information from those Providers
        depending on your individual needs, your requests, your subscription, or
        extenuating circumstances such as when we have a reasonable belief that
        such collection is necessary to protect health or safety. Except for
        Providers providing healthcare services, from whom we will never request
        or obtain PHI, we may collect personal information from you intended for
        your applicable Provider. Apart from information you share on the bQuest
        Platform via messages with a Provider, we do not obtain personal
        information about you from Providers. Do not share any personal
        information with a Provider on the bQuest platform. Similarly, if you
        are a Provider on the bQuest Platform, you must not share any sensitive
        personal information with a User, Client, or Member via the messaging
        capability on the bQuest Platform; failure to comply with the foregoing
        section may be grounds for immediate termination or suspension from the
        bQuest platform.
      </BulletPoint>
      <br />

      <BulletPoint>
        Cookies
        <br />
        bQuest may automatically collect personal information and data through
        the use of cookies. Cookies are small text files a website uses to
        recognize repeat users, facilitate the user's ongoing access to a
        website, and facilitate the use of a website. Cookies also allow a
        website to track usage behavior and compile site usage information that
        will allow for the improvement of content and target advertising. Most
        browsers allow you to control cookies, including whether to accept them
        and how to remove them. You may set your browsers to notify you if you
        receive a cookie, or you may choose to block cookies with your browser.
        However, please be aware that some features of our Services may not
        function or may be slower if you refuse cookies. You may also get
        cookies from advertisers or other third parties with links on the bQuest
        Platform. We do not directly control these cookies. To learn more, or to
        opt-out of receiving online display advertisements tailored to your
        interests by our third party partners, visit the Digital Advertising
        Alliance Consumer Choice Page at http://www.aboutads.info/choices/.
      </BulletPoint>
      <br />

      <BulletPoint>
        Other Tracking Technologies
        <br />
        Tracking technologies may record information such as Internet domain and
        host names; internet protocol (IP) addresses; browser software and
        operating system types; click patterns; the MAC address or other
        technical information from your mobile device; and dates and times that
        our Services are accessed. This technology allows us to understand which
        pages you visit on the bQuest Platform and other ways you interact with
        our Services, such as purchases made through the Services. These tracer
        tags are used to help us to optimize and tailor our Services for you and
        other users of our Services. We may link the information we record using
        tracking technologies to personal information we collect.
      </BulletPoint>
      <br />

      <BulletPoint>
        Do Not Track
        <br />
        Do Not Track (DNT) is a privacy preference that users can set in some
        web browsers, allowing users to opt out of tracking by websites and
        online services. At the present time, the World Wide Web Consortium
        (W3C) has not yet established universal standards for recognizable DNT
        signals; therefore bQuest and the bQuest Platform do not recognize DNT.
      </BulletPoint>
      <br />
    </div>
  </>
);
